import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClockIcon, AlertTriangleIcon, CheckCircleIcon, TrendingUpIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const SLA = () => {
  const { t } = useLanguage();
  const slaData = [
    {
      typeKey: "incident",
      type: t.sla.typeIncident,
      icon: <AlertTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />,
      description: t.sla.typeIncidentDesc,
      priorities: [
        { levelKey: "critical", level: t.dashboard.priorityCritical, response: "1h", resolution: "4h", description: t.sla.incidentCriticalDesc },
        { levelKey: "high", level: t.dashboard.priorityHigh, response: "2h", resolution: "8h", description: t.sla.incidentHighDesc },
        { levelKey: "medium", level: t.dashboard.priorityMedium, response: "4h", resolution: "24h", description: t.sla.incidentMediumDesc },
        { levelKey: "low", level: t.dashboard.priorityLow, response: "8h", resolution: "48h", description: t.sla.incidentLowDesc }
      ]
    },
    {
      typeKey: "request",
      type: t.sla.typeRequest,
      icon: <CheckCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />,
      description: t.sla.typeRequestDesc,
      priorities: [
        { levelKey: "critical", level: t.dashboard.priorityCritical, response: "2h", resolution: "8h", description: t.sla.requestCriticalDesc },
        { levelKey: "high", level: t.dashboard.priorityHigh, response: "4h", resolution: "24h", description: t.sla.requestHighDesc },
        { levelKey: "medium", level: t.dashboard.priorityMedium, response: "8h", resolution: "48h", description: t.sla.requestMediumDesc },
        { levelKey: "low", level: t.dashboard.priorityLow, response: "24h", resolution: "48h", description: t.sla.requestLowDesc }
      ]
    },
    {
      typeKey: "problem",
      type: t.sla.typeProblem,
      icon: <TrendingUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />,
      description: t.sla.typeProblemDesc,
      priorities: [
        { levelKey: "critical", level: t.dashboard.priorityCritical, response: "2h", resolution: "8h", description: t.sla.problemCriticalDesc },
        { levelKey: "high", level: t.dashboard.priorityHigh, response: "4h", resolution: "24h", description: t.sla.problemHighDesc },
        { levelKey: "medium", level: t.dashboard.priorityMedium, response: "8h", resolution: "48h", description: t.sla.problemMediumDesc },
        { levelKey: "low", level: t.dashboard.priorityLow, response: "24h", resolution: "48h", description: t.sla.problemLowDesc }
      ]
    },
    {
      typeKey: "change",
      type: t.sla.typeChange,
      icon: <ClockIcon className="h-5 w-5 text-green-600 dark:text-green-400" />,
      description: t.sla.typeChangeDesc,
      priorities: [
        { levelKey: "critical", level: t.dashboard.priorityCritical, response: "4h", resolution: "24h", description: t.sla.changeCriticalDesc },
        { levelKey: "high", level: t.dashboard.priorityHigh, response: "8h", resolution: "48h", description: t.sla.changeHighDesc },
        { levelKey: "medium", level: t.dashboard.priorityMedium, response: "24h", resolution: "48h", description: t.sla.changeMediumDesc },
        { levelKey: "low", level: t.dashboard.priorityLow, response: "48h", resolution: "240h", description: t.sla.changeLowDesc }
      ]
    }
  ];

  const getPriorityColor = (levelKey: string) => {
    switch (levelKey) {
      case "critical": return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200";
      case "high": return "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200";
      case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200";
      case "low": return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t.sla.title}</h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-3xl">
            {t.sla.subtitle}
          </p>
        </div>

        {/* Informações Gerais */}
        <Card className="mb-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.sla.howItWorks}</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">{t.sla.responseTime}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t.sla.responseTimeDesc}
              </p>
              
              <h3 className="font-semibold mb-2 text-green-600 dark:text-green-400">{t.sla.resolutionTime}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t.sla.resolutionTimeDesc}
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-2 text-purple-600 dark:text-purple-400">{t.sla.serviceHours}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <strong>{t.sla.weekdays}</strong>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                <strong>{t.sla.emergencies}</strong>
              </p>
              
              <h3 className="font-semibold mb-2 text-orange-600 dark:text-orange-400">{t.sla.slaCalculation}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t.sla.slaCalculationDesc}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabelas de SLA por Tipo */}
        <div className="space-y-8">
          {slaData.map((typeData) => (
            <Card key={typeData.typeKey} className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center dark:text-white">
                  {typeData.icon}
                  <span className="ml-2">{typeData.type}</span>
                </CardTitle>
                <CardDescription className="dark:text-gray-400">{typeData.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b dark:border-gray-600">
                        <th className="text-left py-3 px-4 dark:text-gray-200">{t.sla.priority}</th>
                        <th className="text-left py-3 px-4 dark:text-gray-200">{t.sla.response}</th>
                        <th className="text-left py-3 px-4 dark:text-gray-200">{t.sla.resolution}</th>
                        <th className="text-left py-3 px-4 dark:text-gray-200">{t.sla.description}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {typeData.priorities.map((priority) => (
                        <tr key={priority.levelKey} className="border-b hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50">
                          <td className="py-3 px-4">
                            <Badge className={getPriorityColor(priority.levelKey)}>
                              {priority.level}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium text-blue-600 dark:text-blue-400">{priority.response}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium text-green-600 dark:text-green-400">{priority.resolution}</span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {priority.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Notas Importantes */}
        <Card className="mt-8 dark:bg-gray-800 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="dark:text-white">{t.sla.notesTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t.sla.criticalNote}</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t.sla.communicationNote}</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t.sla.escalationNote}</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t.sla.slaPauseNote}</p>
            </div>
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">{t.sla.reopenNote}</p>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default SLA;
